/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { create } from 'zustand';
import { advanceAuthGeneration, getAuthSnapshot } from '@adp-arch/bot-http';
import {
  type UserAuthInfo,
  type UserLabel,
} from '@adp-arch/bot-api/developer_api';
import { DeveloperApi, PlaygroundApi } from '@adp-arch/bot-api';

import { type UserInfo } from '../types';

export interface UserStoreState {
  isSettled: boolean;
  hasError: boolean;
  userInfo: UserInfo | null;
  userAuthInfos: UserAuthInfo[];
  userLabel: UserLabel | null;
}

export interface UserStoreAction {
  reset: () => void;
  setIsSettled: (isSettled: boolean) => void;
  setUserInfo: (userInfo: UserInfo | null) => void;
  authenticate: (userInfo: UserInfo) => void;
  getUserAuthInfos: () => Promise<void>;
}

export const defaultState: UserStoreState = {
  isSettled: false,
  userInfo: null,
  hasError: false,
  userAuthInfos: [],
  userLabel: null,
};

export const useUserStore = create<UserStoreState & UserStoreAction>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      ...defaultState,
      reset: () => {
        set({ ...defaultState, isSettled: true });
        advanceAuthGeneration(false);
      },
      setIsSettled: isSettled => {
        set({
          isSettled,
        });
      },
      authenticate: userInfo => {
        const previousId = get().userInfo?.user_id_str;
        set({ userInfo, isSettled: true, hasError: false });
        advanceAuthGeneration(Boolean(userInfo.user_id_str));
        if (userInfo.user_id_str && userInfo.user_id_str !== previousId) {
          void fetchUserLabel(userInfo.user_id_str);
        }
      },
      setUserInfo: (userInfo: UserInfo | null) => {
        if (
          userInfo?.user_id_str &&
          userInfo?.user_id_str !== get().userInfo?.user_id_str
        ) {
          fetchUserLabel(userInfo?.user_id_str);
        }
        set({
          userInfo,
        });
      },
      getUserAuthInfos: async () => {
        const { generation } = getAuthSnapshot();
        const { data = [] } = await DeveloperApi.GetUserAuthList();
        if (generation === getAuthSnapshot().generation) {
          set({ userAuthInfos: data });
        }
      },
    })),
    {
      enabled: IS_DEV_MODE,
      name: 'botStudio.userStore',
    },
  ),
);

const fetchUserLabel = async (id: string) => {
  const { generation } = getAuthSnapshot();
  const res = await PlaygroundApi.MGetUserBasicInfo({ user_ids: [id] });
  const userLabel = res?.id_user_info_map?.[id]?.user_label;
  if (
    generation === getAuthSnapshot().generation &&
    useUserStore.getState().userInfo?.user_id_str === id
  ) {
    useUserStore.setState({ userLabel });
  }
};
